import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Icon, Popover, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import _ from 'underscore';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../context/complexDocumentContext';
import { PageElementsContext } from '../../../context/pageElementsContext';
import FillableSettings from '../../../components/FillableSettings';
import { BorderButton, ColorButton, LineWeightInput, LineWeightLabel, LineWeightSlider, LineWeightSliderWrapper } from '../../../artboard/Toolbar/styles';
import ColorPicker from '../../../artboard/ColorPicker';
import TextBlockWithQuillRenderer from '../TextBlockWithQuillRenderer';
import { useDefaultStyleContext } from '../../../context/complexDefaultStyleContext';
import { constructQuillJSON, constructAutoSaveJSON, convertContractToReact } from '../../../utils/TextBlockHelper';
import { ELEMENTS_SETTINGS_TOOLBAR } from '../../../artboard/Toolbar/translationValues';
import { OPTIONS_KEY, FIELD_NAME_KEY, READ_ONLY_KEY, INPUT_TEXT_ELEMENT_OPTIONS,
   TEXT_DEBOUNCE_TIME_OUT } from '../../../utils/complexDocumentConstants';
import { CONTEXT_KEYS, useArtboardContext } from '../../../context/complexArtboardContext';
import { FillableElementWrapper } from '../styles';
const { MULTILINE_KEY, MAX_CHARACTERS_KEY } = INPUT_TEXT_ELEMENT_OPTIONS

const NAMESPACES = ['ELEMENTS_SETTINGS_TOOLBAR'];
const getElementDetails = el => el;

const TextInputEditor = (props) => {
  const { element, selected, editable, staticTableBlock } = props;
  const { t } = useTranslation(NAMESPACES);
  const [picker, setPicker] = useState(false);
  const [textEditable, setTextEditable] = useState(false);
  const containerRef = useRef(null);
  const fontChangeRef = useRef(null);
  const documentStyleRef = useRef(null);
  const [showBorderWidthPopover, setBorderWidthPopover] = useState(false);
  const [borderWidth, setBorderWidth] = useState(0);

  const toggleBorderWidthPopover = useCallback(() => {
    setBorderWidthPopover(previousValue => !previousValue);
    if (element?.style?.border?.thickness) {
      setBorderWidth(element?.style?.border?.thickness);
    }
  }, [element]);

  const handleBorderWidthChange = useCallback(updatedBorderWidth => {
    updateComplexElementDetails(element.uuid, { border: { ...element.style.border, thickness: updatedBorderWidth} } ,'style');
    setBorderWidth(updatedBorderWidth);
  }, []);

  const { updateComplexElementDetails } = useContext(PageElementsContext);
  const customVarSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], []);
  const [globalVariables] = useDocumentContext(
    customVarSelector,
    `TextInputEditor-${element.uuid}`
  );

  const getDefaultStyleSelector = useCallback(store => ({fontFamily: store.fontFamily, fontSize:store.fontSize}), []);
  const [documentStyle] = useDefaultStyleContext(getDefaultStyleSelector);

  const [openFontSettings, toggleFontSettings] = useState(false);
  const debouncedUpdateComplexElementDetails = useRef(_.debounce(updateComplexElementDetails, TEXT_DEBOUNCE_TIME_OUT)).current;

  const [, updateArtboard] = useArtboardContext(() => null);
  useEffect(() => {
    // close the sidebar when the element is removed from the block.
    return () => {
      updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false });
    };
  }, []);

  const showBackgroundColorPicker = useCallback(() => {
    setPicker(currentValue => {
      if (currentValue && currentValue.type === 'backgroundColor') {
        return false;
      }
      return { type: 'backgroundColor', color: element.style.backgroundColor };
    });
  }, [element]);

  const showBorderColorPicker = useCallback(() => {
    setPicker(currentValue => {
      if (currentValue && currentValue.type === 'borderColor') {
        return false;
      }
      return { type: 'borderColor', color: element.style.border?.borderColor };
    });
  }, [element]);

  const onColorPick = useCallback(color => {
    if (picker.type === 'backgroundColor') {
      updateComplexElementDetails(element.uuid, { backgroundColor: color.hex } ,'style');
      setPicker({ type: 'backgroundColor', color: color.hex });
    } else if (picker.type === 'borderColor') {
      updateComplexElementDetails(element.uuid, { border: { ...element.style.border, borderColor: color.hex } } ,'style');
      setPicker({ type: 'borderColor', color: color.hex });
    }
  }, [picker]);

  const updateElementDetails = useCallback((
    value,
    key
  ) => {
    const details = { options : { [key]: value } };
    if (key === MULTILINE_KEY && !staticTableBlock) {
      details.style = {};
      if (value) {
        details.style.width = '500px';
        details.style.height = '150px';
        details.style.backgroundColor = '#ffffff';
      } else {
        details.style.width = '250px';
        details.style.height = '26px';
        details.style.backgroundColor = '#e7e5e5';
      }
    }
    if (key === MAX_CHARACTERS_KEY) {
      debouncedUpdateComplexElementDetails(element.uuid, details, 'form-options');
    } else {
      updateComplexElementDetails(element.uuid, details, 'form-options');
    }
  }, [updateComplexElementDetails]);

  const onFieldNameChange = useCallback((value) => {
    // TODO : Check for duplicates
      debouncedUpdateComplexElementDetails(element.uuid, { options: { [FIELD_NAME_KEY]: value } }, 'form-options');
  }, []);

  const saveBlock = useCallback((contents, undoDiff, redoDiff, cb, parentStyle = {}, op = undefined) => {
    // TODO : Understand if height change / scaling has to be captured from text typing changes
    if(fontChangeRef.current){
      fontChangeRef.current=false;
      cb();
      return;
    };
    updateComplexElementDetails(element.uuid, {
      contents: constructAutoSaveJSON(contents, documentStyle?.fontFamily, documentStyle?.fontSize, globalVariables),
      style: { ...parentStyle },
      undoDiff,
      redoDiff,
      op
    }, 'text-value-with-style');
    cb();
  }, []);

  useEffect(() => {
    const handleFontStyleUpdate = e => {
      if(!editable) return;
      const { action, newFontFamilyOrSize, styleType } = e.detail;
      const eventData =
        action === 'update'
          ? {
              fontStyleData: newFontFamilyOrSize,
              styleType,
              action,
            }
          : { action };
      if (containerRef?.current) {
        fontChangeRef.current = true;
        documentStyleRef.current = Object.assign({}, documentStyleRef.current,{[styleType]:newFontFamilyOrSize});
        setTextEditable(true);
        setTimeout(() => {
          containerRef.current.dispatchEvent(
            new CustomEvent('updateFontFamilySizeEvent', { detail: eventData }),
          );
        }, 500);
      }
    };
    documentStyleRef.current = documentStyle;
    document.addEventListener('textElementFontStyleUpdate', handleFontStyleUpdate);
    return () => {
      document.removeEventListener('textElementFontStyleUpdate', handleFontStyleUpdate);
    };
  }, []);

  useEffect(() => {
    if (editable) {
      setTextEditable(true);
    }
  }, [editable]);

  return (
    <>
      <TextBlockWithQuillRenderer
        ref={containerRef}
        defaultContent={constructQuillJSON(
          element?.contents,
          documentStyleRef.current?.fontFamily || documentStyle?.fontFamily,
          documentStyleRef.current?.fontSize || documentStyle?.fontSize,
          globalVariables,
        )}
        saveBlock={saveBlock}
        isFromCreativeBlock={true}
        editable={editable}
        itemUUID={element.uuid}
        selected={selected}
        enableSideBar={toggleFontSettings}
        customClassName={element[OPTIONS_KEY][MULTILINE_KEY] ? 'multiline' : 'textBox'}
        textEditable={textEditable}
        setTextEditable={setTextEditable}
        children={!textEditable ? convertContractToReact(element?.contents, documentStyle?.fontFamily, documentStyle?.fontSize, globalVariables) : null}
      />
      {editable && selected && (
        <>
        {!openFontSettings && ReactDOM.createPortal(
          <FillableSettings
            style={{ top: 10 }}
            element={element}
            updateElementDetails={updateElementDetails}
            onFieldNameChange={onFieldNameChange}
            OPTIONS_KEY={OPTIONS_KEY}
            FIELD_NAME_KEY={FIELD_NAME_KEY}
            MULTILINE_KEY={MULTILINE_KEY}
            READ_ONLY_KEY={READ_ONLY_KEY}
            MAX_CHARACTERS_KEY={MAX_CHARACTERS_KEY}
            getElementDetails={getElementDetails}
          />,
          document.getElementById('sidebar-absolute-container')
        )}
        {ReactDOM.createPortal(
          (<FillableElementWrapper>
            <Tooltip
              placement="bottom"
              title={t(ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BACKGROUND_TOOLTIP.key, ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BACKGROUND_TOOLTIP.value)}
            >
              <ColorButton
                style={{ backgroundColor: element.style.backgroundColor, position: 'relative' }}
                onClick={showBackgroundColorPicker}
              />
            </Tooltip>
            <Tooltip
              placement="bottom"
              title={t(ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BORDER_COLOR_TOOLTIP.key, ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BORDER_COLOR_TOOLTIP.value)}
            >
              <ColorButton
                style={{ backgroundColor: element.style.border?.borderColor, position: 'relative' }}
                onClick={showBorderColorPicker}
              />
            </Tooltip>
            {picker && (
              <div style={{ position: 'absolute', top: '100%', height: '366px' }}>
                <ColorPicker
                  onChange={onColorPick}
                  onBlur={() => setPicker(false)}
                  color={picker.color}
                  qlEditor={true}
                />
              </div>
            )}
            {
              <Popover
                content={
                  <LineWeightSliderWrapper>
                    <LineWeightLabel>
                      <p>
                        {t(
                          ELEMENTS_SETTINGS_TOOLBAR.BORDER_WIDTH_LABEL.key,
                          ELEMENTS_SETTINGS_TOOLBAR.BORDER_WIDTH_LABEL.value,
                        )}
                      </p>
                      <LineWeightSlider
                        min={0}
                        max={100}
                        onChange={handleBorderWidthChange}
                        value={typeof borderWidth !== 'undefined' ? borderWidth : 1}
                      />
                      <LineWeightInput
                        min={0}
                        max={100}
                        style={{ marginLeft: 16 }}
                        value={borderWidth}
                        onChange={handleBorderWidthChange}
                      />
                    </LineWeightLabel>
                  </LineWeightSliderWrapper>
                }
                trigger="click"
                placement="bottomLeft"
                visible={showBorderWidthPopover}
                onVisibleChange={setBorderWidthPopover}
              >
                <Tooltip
                  placement="bottom"
                  title={t(ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BORDER_WIDTH_LABEL.key, ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BORDER_WIDTH_LABEL.value)}
                >
                  <BorderButton onClick={toggleBorderWidthPopover}>
                    <Icon type={'border'} style={{ fontSize: 18 }} />
                  </BorderButton>
                </Tooltip>
              </Popover>
              }
          </FillableElementWrapper>),
          document.getElementById('document-editor-toolbar'),
        )}
      </>)}
    </>
  )
}

export default TextInputEditor;